// File: frontend/src/pages/dashboards/StudentDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/axios';

const StudentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [attendanceRate, setAttendanceRate] = useState(96.0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const [attRes] = await Promise.all([
          api.get('/api/v1/parent/attendance').catch(() => ({ data: null }))
        ]);

        if (attRes.data?.success && attRes.data.data?.attendancePercentage) {
          setAttendanceRate(attRes.data.data.attendancePercentage);
        }
      } catch (error) {
        console.error('Error loading student dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, []);

  const studentName = user?.name || user?.email || 'Student';

  return (
    <DashboardLayout>
      {/* Sub Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Student Portal</h1>
          <p className="text-xs text-purple-600 font-semibold mt-0.5">
            Dashboard <span className="text-slate-400">/</span> Academic Progress & Grades
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate('/my-timetable')}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-xl shadow-purple-glow transition"
          >
            🗓️ My Class Routine
          </button>
        </div>
      </div>

      {/* Welcome Student Banner */}
      <div className="bg-purple-gradient rounded-3xl p-6 sm:p-8 text-white shadow-xl">
        <span className="px-3 py-1 bg-white/10 text-purple-200 rounded-full text-[10px] font-extrabold uppercase tracking-wider border border-white/10">
          STUDENT DESK
        </span>
        <h2 className="text-2xl sm:text-3xl font-extrabold mt-2">
          Welcome back, {studentName}! 🎓
        </h2>
        <p className="text-purple-200 text-xs sm:text-sm mt-1 max-w-xl">
          Check your attendance percentage, view your report card grades, and review upcoming exam schedules.
        </p>
      </div>

      {/* Top Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white rounded-3xl p-5 border border-purple-100 shadow-purple-subtle">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-purple-600 uppercase">Attendance</span>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Good</span>
          </div>
          <h3 className="text-2xl font-extrabold text-slate-900">{loading ? '...' : `${attendanceRate}%`}</h3>
          <p className="text-xs text-slate-500 mt-1">Class Presence Rate</p>
        </div>

        <div
          onClick={() => navigate('/my-timetable')}
          className="bg-white rounded-3xl p-5 border border-purple-100 shadow-purple-subtle hover:shadow-md cursor-pointer transition"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-cyan-600 uppercase">Schedule</span>
            <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-bold">Routine</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-900">My Timetable</h3>
          <p className="text-xs text-slate-500 mt-1">Daily period schedule</p>
        </div>

        <div
          onClick={() => navigate('/parent/results')}
          className="bg-white rounded-3xl p-5 border border-purple-100 shadow-purple-subtle hover:shadow-md cursor-pointer transition"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-purple-600 uppercase">Grades</span>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">GPA</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-900">Report Card</h3>
          <p className="text-xs text-slate-500 mt-1">View exam results</p>
        </div>

        <div
          onClick={() => navigate('/parent/fees')}
          className="bg-white rounded-3xl p-5 border border-purple-100 shadow-purple-subtle hover:shadow-md cursor-pointer transition"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-amber-600 uppercase">Fees</span>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">Ledger</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-900">Fee Receipts</h3>
          <p className="text-xs text-slate-500 mt-1">Payment records</p>
        </div>
      </div>

      {/* Grids */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-purple-subtle space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-purple-100 pb-3">Recent Grades</h3>
          <div className="space-y-3">
            <div className="p-3.5 bg-purple-50/60 rounded-2xl border border-purple-100 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-900 text-xs">Mathematics Term Exam</h4>
                <p className="text-[10px] text-slate-500">Marks: 88 / 100</p>
              </div>
              <span className="px-2.5 py-1 bg-emerald-500 text-white font-extrabold text-xs rounded-xl">Grade A+</span>
            </div>
            <div className="p-3.5 bg-purple-50/60 rounded-2xl border border-purple-100 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-900 text-xs">Computer Science Practical</h4>
                <p className="text-[10px] text-slate-500">Marks: 94 / 100</p>
              </div>
              <span className="px-2.5 py-1 bg-emerald-500 text-white font-extrabold text-xs rounded-xl">Grade A+</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-purple-subtle space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-purple-100 pb-3">Upcoming Examinations</h3>
          <div className="space-y-3">
            <div className="p-3.5 bg-purple-50/60 rounded-2xl border border-purple-100 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-900 text-xs">Annual Final Exams 2024</h4>
                <p className="text-[10px] text-purple-600 font-semibold">Starts: 15 October 2024</p>
              </div>
              <span className="px-2.5 py-1 bg-purple-600 text-white font-bold text-[10px] rounded-xl">Upcoming</span>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;
